'use client';

/*
Page d'atterrissage CMS (Sanity) — charte v5, sur le modèle de la homepage (components/pages/home.js).
Sert les trois catégories : activité (/activity, /fr/activite, /es/actividad), ville (/city,
/fr/ville, /es/ciudad) et type de lieu (/type, /fr/type, /es/tipo) ; seule la copie change
(components/cms/landingCopy.js).
Sections : hero + recherche, bandeau de preuve, intro (partie 0 du CMS), lieux taggés
`<catégorie>_<slug>` (paginés), guide (parties 1 et suivantes du CMS), brief, liens vers les
autres pages de la catégorie, CTA. Les pages > 1 ne rendent que le hero compact, les lieux et le
CTA : l'ancien gabarit répétait les mêmes textes en haut et en bas de chaque page.
*/

import { useEffect, useMemo } from 'react';
import Image from 'next/image';
import { PortableText } from '@portabletext/react';
import { lang as useLang, hocTranslateServerClient } from '@/helpers/translate';
import { getPathByLang, getRootUrl } from '@/helpers/router';
import { useZusHomeStore } from '@/helpers/context';
import { useZusTrackerStore } from '@/helpers/tracker.js';
import { FadeUp, Stagger, StaggerChild } from '@/core/motion';
import HeroSearch from '@/components/home/heroSearch';
import SelectionCard from '@/components/home/selectionCard';
import { useHomeCopy } from '@/components/home/homeCopy';
import {
  CARD, SECTION, H2, ArrowIcon, SearchIcon, Kicker, ProofBand,
} from '@/components/home/primitives';
import { useLandingCopy } from '@/components/cms/landingCopy';
import CmsPaginator from '@/app/cms/paginator';

// Racine des pages de chaque catégorie par locale (cf. les dossiers app/<locale>/…).
const LANDING_BASE = {
  activity: { en: '/activity', fr: '/fr/activite', es: '/es/actividad' },
  city: { en: '/city', fr: '/fr/ville', es: '/es/ciudad' },
  type: { en: '/type', fr: '/fr/type', es: '/es/tipo' },
};

// Le CMS ne porte qu'un lien Pexels en fond. Les activités ont un visuel « usages » de la
// homepage ; sinon (villes, types) on prend la photo du premier lieu de la page, qui montre un
// vrai décor de la ville ou du type ; à défaut, le visuel du hero de la homepage.
function curatedHero(category, slug = '') {
  if (category !== 'activity') return null;
  if (/shoot|photo|foto/.test(slug)) return '/img/home-v5/usage-shooting.jpg';
  if (/tourn|film|rodaje|video/.test(slug)) return '/img/home-v5/usage-cinema.jpg';
  return null;
}

function spaceHero(spaces) {
  const picture = (spaces || []).find((s) => s?.pictures?.[0]?.nameId)?.pictures[0];
  const engine = process.env.NEXT_PUBLIC_IMAGER_ENGINE;
  if (!picture || !engine) return null;
  const ext = useZusHomeStore.getState().extWebpJpg || 'webp';
  return `${engine}/${picture.nameId}-1600-*-${picture.extension}.${ext}`;
}

// Header transparent (liens blancs) sur le hero, blanc dès qu'on défile : même mécanique que la
// homepage (`lightScrolled` dans le store, cf. helpers/context.js) ; on la retire en quittant.
function useHeroHeader() {
  useEffect(() => {
    const { setHeaderBackground2 } = useZusHomeStore.getState();
    useZusHomeStore.setState({ lightScrolled: true });
    setHeaderBackground2(window.scrollY < 50 ? 'style1' : 'style2');
    return () => {
      useZusHomeStore.setState({ lightScrolled: false });
    };
  }, []);
}

function Heading({ children }) {
  return <h3 className="mb-3 mt-9 text-[20px] font-bold leading-[1.25] tracking-[-0.02em] text-was-ink first:mt-0">{children}</h3>;
}

function Paragraph({ children }) {
  return <p className="m-0 mb-5 text-[16px] leading-[1.7] text-was-slate last:mb-0">{children}</p>;
}

function ListItem({ children }) {
  return <li className="pl-1">{children}</li>;
}

// Rendu du Portable Text Sanity dans la typographie v5 (les rédacteurs utilisent h4 pour les intertitres).
const PT_COMPONENTS = {
  block: {
    normal: Paragraph,
    h1: Heading,
    h2: Heading,
    h3: Heading,
    h4: Heading,
    h5: Heading,
    blockquote: ({ children }) => <blockquote className="my-6 border-l-2 border-was-violet pl-5 text-[18px] font-light leading-[1.5] text-was-ink">{children}</blockquote>,
  },
  list: {
    bullet: ({ children }) => <ul className="m-0 mb-5 list-disc space-y-1.5 pl-5 text-[16px] leading-[1.7] text-was-slate marker:text-was-violet">{children}</ul>,
    number: ({ children }) => <ol className="m-0 mb-5 list-decimal space-y-1.5 pl-5 text-[16px] leading-[1.7] text-was-slate marker:text-was-violet">{children}</ol>,
  },
  listItem: {
    bullet: ListItem,
    number: ListItem,
  },
  marks: {
    strong: ({ children }) => <strong className="font-bold text-was-ink">{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    link: ({ value, children }) => {
      const href = value?.href || '#';
      const external = /^https?:\/\//.test(href) && !href.includes('wearescene');
      return (
        <a href={href} className="text-was-violet underline decoration-was-violet/40 underline-offset-4 transition-colors hover:text-was-violet hover:decoration-was-violet" target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>
          {children}
        </a>
      );
    },
  },
};

function RichText({ value, className }) {
  if (!value?.length) return null;
  return (
    <div className={className}>
      <PortableText value={value} components={PT_COMPONENTS} />
    </div>
  );
}

function HeroSection({
  heroSrc, cms, tx, subtitle, features, compact,
}) {
  const remote = /^https?:\/\//.test(heroSrc);
  return (
    <section className={`was-grain relative flex flex-col justify-center bg-was-night ${compact ? 'min-h-[60svh]' : 'min-h-[88svh]'}`}>
      <div className="absolute inset-0 overflow-hidden">
        <div className="was-hero-zoom absolute inset-0">
          <Image src={heroSrc} alt="" fill priority sizes="100vw" quality={remote ? 72 : 75} className="object-cover object-center" />
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(135deg,rgba(30,22,48,0.92)_0%,rgba(30,22,48,0.66)_45%,rgba(19,14,27,0.32)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-3/5 bg-[linear-gradient(180deg,rgba(19,14,27,0)_0%,rgba(19,14,27,0.55)_100%)]" />
      {/* Dégradé en tête pour la lisibilité du header transparent (logo, liens). */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-56 bg-[linear-gradient(180deg,rgba(19,14,27,0.72)_0%,rgba(19,14,27,0.35)_55%,rgba(19,14,27,0)_100%)]" />
      <div className="relative mx-auto w-full max-w-[1240px] px-6 pb-24 pt-28 lg:px-8 lg:pb-16 lg:pt-36">
        <div className="was-fade-up flex items-center gap-3 text-sm font-normal text-white/70">
          <span className="first-letter:uppercase">{tx('kicker')}</span>
          <span className="h-px w-6 bg-white/30" />
          <span>{cms.linktext || cms.title}</span>
        </div>
        <h1 className="was-fade-up m-0 mt-5 max-w-[900px] text-balance text-[30px] font-heading font-extrabold uppercase tracking-[-0.01em] leading-[1.04] text-white md:text-[42px] lg:text-[50px]" style={{ animationDelay: '60ms' }}>{(cms.title || '').trim()}</h1>
        {!compact && (
          <p className="was-fade-up mt-5 max-w-[700px] text-lg font-normal leading-[1.45] text-white [text-shadow:0_1px_2px_rgba(19,14,27,0.7),0_4px_24px_rgba(19,14,27,0.7)] md:text-[22px]" style={{ animationDelay: '120ms' }}>{subtitle}</p>
        )}
        {/* z-10 : les menus (hubs, calendrier, mots-clés) doivent passer au-dessus des repères. */}
        <div className="was-fade-up relative z-10 mt-9 lg:mt-11" style={{ animationDelay: '200ms' }}>
          <HeroSearch />
        </div>
        {!compact && (
          <div className="was-fade-up relative z-[5]" style={{ animationDelay: '320ms' }}>
            <ul className="mt-4 hidden flex-wrap items-center gap-x-3.5 gap-y-1.5 pl-2 md:flex">
              {features.map((label, i) => (
                <li key={`landingfeature${label}`} className="inline-flex items-center gap-3.5 text-[13px] font-normal tracking-[0.02em] text-white/70">
                  {label}
                  {i < features.length - 1 && <span className="size-[3px] rounded-full bg-white/40" />}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

// Partie 0 du CMS. Avec un titre (activités) : titre à gauche, texte à droite ; sans titre
// (villes, types) : le texte seul, en chapeau, plutôt que de répéter le H1.
function IntroSection({ cms, tx }) {
  if (!cms.part_0_title && !cms.part_0_desc?.length) return null;
  return (
    <section className="bg-was-cream">
      <div className={SECTION}>
        {cms.part_0_title ? (
          <div className="grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
            <FadeUp>
              <Kicker n="01" scene={tx('sceneLabel')}>{tx('intro.kicker')}</Kicker>
              <h2 className={`mt-5 ${H2}`}>{cms.part_0_title}</h2>
            </FadeUp>
            <FadeUp delay={0.1}>
              <RichText value={cms.part_0_desc} className="max-w-[62ch] [&_p]:text-[17px]" />
            </FadeUp>
          </div>
        ) : (
          <FadeUp>
            <Kicker n="01" scene={tx('sceneLabel')}>{tx('intro.kicker')}</Kicker>
            <RichText value={cms.part_0_desc} className="mt-8 max-w-[70ch] [&_p]:text-[19px] [&_p]:leading-[1.6] [&_p]:text-was-ink md:[&_p]:text-[21px]" />
          </FadeUp>
        )}
      </div>
    </section>
  );
}

function SpacesSection({
  tx, homeTx, spaces, total, perPage, page, name, searchHref,
}) {
  if (!spaces?.length) return null;
  const isFirstPage = page <= 1;
  return (
    <section id="lieux" className="border-t border-was-ink/8 bg-white">
      <div className={SECTION}>
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
          <FadeUp>
            <Kicker n={isFirstPage ? '02' : null} scene={tx('sceneLabel')}>
              {tx('spaces.kicker')}
              {!isFirstPage && ` · ${tx('spaces.page', { n: page })}`}
            </Kicker>
            <h2 className={`mt-5 max-w-[900px] ${H2}`}>{tx('spaces.title', { name })}</h2>
          </FadeUp>
          <div className="mb-1.5 flex shrink-0 items-center gap-5">
            {total > 0 && <span className="text-sm tabular-nums text-was-muted">{tx('spaces.count', { n: total })}</span>}
            <a href={searchHref} className="inline-flex items-center gap-2 text-[15px] font-normal text-was-ink underline decoration-was-ink/30 underline-offset-4 transition-colors hover:text-was-ink hover:decoration-was-ink">
              {tx('spaces.all')}
              <ArrowIcon />
            </a>
          </div>
        </div>
        <Stagger stagger={0.06} delay={0.1} className={`mt-10 grid gap-5 md:grid-cols-2 lg:mt-12 lg:grid-cols-3 ${isFirstPage ? 'xl:grid-cols-4' : ''}`}>
          {spaces.map((s) => (
            <StaggerChild key={`landingspace${s.urlByLang || s.title}`} className="h-full">
              <SelectionCard item={s} tx={homeTx} />
            </StaggerChild>
          ))}
        </Stagger>
        <div className="mt-12 flex justify-center lg:mt-16">
          <CmsPaginator total={total} perPage={perPage} />
        </div>
      </div>
    </section>
  );
}

function GuideSection({ parts, tx }) {
  if (!parts.length) return null;
  return (
    <section className="border-t border-was-ink/8 bg-was-cream">
      <div className="mx-auto max-w-[1240px] px-6 py-16 lg:px-8 lg:py-24">
        <FadeUp>
          <Kicker n="03" scene={tx('sceneLabel')}>{tx('guide.kicker')}</Kicker>
          <h2 className={`mt-5 ${H2}`}>{tx('guide.title')}</h2>
        </FadeUp>
        <div className="mt-10 border-y border-was-ink/8 lg:mt-14">
          {parts.map((part, i) => (
            <article key={`landingpart${part.key}`} className={`grid gap-6 py-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20 lg:py-16 ${i > 0 ? 'border-t border-was-ink/8' : ''}`}>
              <FadeUp className="lg:sticky lg:top-28 lg:self-start">
                <span className="text-sm tabular-nums text-was-muted">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="m-0 mt-3 text-balance text-[22px] font-heading font-extrabold uppercase leading-[1.1] tracking-[-0.01em] text-was-ink md:text-[26px]">{part.title}</h3>
              </FadeUp>
              <FadeUp delay={0.08}>
                <RichText value={part.desc} className="max-w-[66ch]" />
              </FadeUp>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function BriefSection({ tx, briefHref }) {
  return (
    <section className="border-t border-was-ink/8 bg-white">
      <div className={SECTION}>
        <FadeUp className={`grid overflow-hidden bg-white lg:grid-cols-[minmax(0,1fr)_400px] ${CARD}`}>
          <div className="px-6 py-8 lg:px-12 lg:py-14">
            <Kicker>{tx('brief.kicker')}</Kicker>
            <h2 className={`mt-5 max-w-[560px] ${H2}`}>{tx('brief.title')}</h2>
            <p className="mt-5 max-w-[520px] text-base leading-relaxed text-was-slate">{tx('brief.desc')}</p>
            <a href={briefHref} className="mt-8 inline-flex items-center gap-2 rounded-lg bg-was-ink px-7 py-3.5 text-[15px] font-normal text-white transition duration-200 hover:bg-was-violet hover:text-white">
              {tx('brief.cta')}
              <ArrowIcon />
            </a>
          </div>
          <div className="relative min-h-[280px] lg:min-h-0">
            <Image src="/img/home-v5/portrait-brief.jpg" alt={tx('brief.imageAlt')} fill sizes="(max-width: 1024px) 100vw, 400px" quality={88} className="object-cover object-[50%_22%]" />
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function OthersSection({ others, tx, base }) {
  if (!others.length) return null;
  return (
    <section className="border-t border-was-ink/8 bg-was-cream">
      <div className="mx-auto max-w-[1240px] px-6 py-14 lg:px-8 lg:py-20">
        <FadeUp>
          <Kicker>{tx('others.kicker')}</Kicker>
          <ul className="m-0 mt-6 flex list-none flex-wrap gap-3 p-0">
            {others.map((a) => (
              <li key={`landingother${a.slug}`}>
                <a href={`${base}/${a.slug}`} className="inline-flex items-center gap-2.5 rounded-lg border border-was-ink/15 bg-white px-5 py-3 text-[15px] font-normal text-was-ink transition duration-200 hover:border-was-ink hover:text-was-ink">
                  {a.linktext || a.title}
                  <ArrowIcon size={14} />
                </a>
              </li>
            ))}
          </ul>
        </FadeUp>
      </div>
    </section>
  );
}

function CtaSection({ tx, searchHref, briefHref }) {
  return (
    <section className="was-grain relative overflow-hidden bg-was-abyss">
      <div className="absolute inset-0">
        <Image src="/img/home-v5/respiration-bts.jpg" alt="" fill sizes="100vw" className="object-cover object-center" />
      </div>
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(10,7,16,0.55)_0%,rgba(10,7,16,0.7)_60%,rgba(10,7,16,0.85)_100%)]" />
      <div className={`relative ${SECTION} lg:py-36`}>
        <FadeUp>
          <h2 className="m-0 max-w-[900px] text-balance text-[28px] font-heading font-extrabold uppercase tracking-[-0.01em] leading-[1.04] text-white md:text-[44px]">{tx('cta.title')}</h2>
          <div className="mt-8 flex flex-wrap gap-4 lg:mt-9">
            <a href={searchHref} className="inline-flex items-center gap-2 rounded-lg bg-white px-7 py-3.5 text-base font-normal text-was-ink transition duration-200 hover:bg-was-cream hover:text-was-ink">
              {tx('cta.search')}
              <SearchIcon />
            </a>
            <a href={briefHref} className="inline-flex items-center gap-2 rounded-lg border border-white/30 px-7 py-3.5 text-base font-normal text-white transition duration-200 hover:border-white hover:text-white">
              {tx('cta.brief')}
            </a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

export default hocTranslateServerClient(({
  t, category, cmsContent, slug, spacesData, getSpacesByTagscounting, perPage, currentPage, siblings,
}) => {
  const langCurrent = useLang();
  const { tx } = useLandingCopy(category);
  const { tx: homeTx } = useHomeCopy();
  const cms = cmsContent || {};
  const page = Number(currentPage) || 1;
  const isFirstPage = page <= 1;
  const searchHref = `${getRootUrl(langCurrent)}/s/paris`;
  const briefHref = getPathByLang('brief', langCurrent);
  const name = (cms.linktext || cms.title || '').trim();
  const others = (siblings || []).filter((a) => a?.slug && a.slug !== slug);
  const total = Number(getSpacesByTagscounting) || 0;
  const base = LANDING_BASE[category]?.[langCurrent] || LANDING_BASE[category]?.en || '';
  const heroSrc = curatedHero(category, slug) || spaceHero(spacesData) || '/img/home-v5/hero-bts.jpg';

  // Parties 1 et suivantes du CMS (la partie 0 sert d'introduction).
  const parts = useMemo(() => [1, 2, 3, 4, 5, 6, 7]
    .map((i) => ({ key: i, title: cms[`part_${i}_title`], desc: cms[`part_${i}_desc`] }))
    .filter((p) => p.title || p.desc?.length), [cms]);

  // Textes du namespace `discovery` (API i18n) quand ils existent, sinon la copie locale.
  const fromApi = (key, fallback) => {
    const value = t(key);
    return value && value !== key ? value : fallback;
  };
  const localFeatures = tx('features') || [];
  const subtitle = fromApi('discovery:subtitle', tx('subtitle'));
  const features = [
    fromApi('discovery:features.places', localFeatures[0]),
    fromApi('discovery:features.find', localFeatures[1]),
    fromApi('discovery:features.exchange', localFeatures[2]),
  ].filter(Boolean);

  useHeroHeader();
  useEffect(() => {
    useZusTrackerStore.getState().setOrEvent('guide_classic');
  }, []);

  return (
    <div className="bg-was-cream font-display text-was-ink antialiased">
      <HeroSection {...{
        heroSrc, cms, tx, subtitle, features, compact: !isFirstPage,
      }}
      />
      {isFirstPage && <ProofBand count={tx('proof.count')} label={tx('proof.label')} />}
      {isFirstPage && <IntroSection cms={cms} tx={tx} />}
      <SpacesSection {...{
        tx, homeTx, spaces: spacesData, total, perPage, page, name, searchHref,
      }}
      />
      {isFirstPage && <GuideSection parts={parts} tx={tx} />}
      {isFirstPage && <BriefSection tx={tx} briefHref={briefHref} />}
      <OthersSection others={others} tx={tx} base={base} />
      <CtaSection tx={tx} searchHref={searchHref} briefHref={briefHref} />
    </div>
  );
});
